import React, { useState } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    Typography,
    Stack,
    Paper,
    DialogContentText,
} from '@mui/material';
import {
    DragIndicator,
    Delete,
    Add,
    Edit,
    Check,
    Close,
} from '@mui/icons-material';
import { collection, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { Space, Plant } from '../types/shared';
import { useTranslation } from 'react-i18next';

interface SortableSpaceItemProps {
    space: Space;
    onEdit: (space: Space) => void;
    onDelete: (space: Space) => void;
    editingId: string | null;
    editName: string;
    editDescription: string;
    onEditNameChange: (value: string) => void;
    onEditDescriptionChange: (value: string) => void;
    onSaveEdit: () => void;
    onCancelEdit: () => void;
}

const SortableSpaceItem: React.FC<SortableSpaceItemProps> = ({
    space,
    onEdit,
    onDelete,
    editingId,
    editName,
    editDescription,
    onEditNameChange,
    onEditDescriptionChange,
    onSaveEdit,
    onCancelEdit,
}) => {
    const { t } = useTranslation();
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: space.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const isEditing = editingId === space.id;

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            elevation={isDragging ? 4 : 1}
            sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                borderRadius: 2,
                bgcolor: isDragging ? 'action.hover' : 'background.paper',
            }}
        >
            <Box
                {...attributes}
                {...listeners}
                sx={{
                    cursor: 'grab',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.secondary',
                    mt: isEditing ? 1 : 0,
                    touchAction: 'none',
                }}
            >
                <DragIndicator fontSize="small" />
            </Box>

            {isEditing ? (
                <Box sx={{ flex: 1 }}>
                    <Stack spacing={1.5}>
                        <TextField
                            size="small"
                            label={t('spaces.name_label')}
                            value={editName}
                            onChange={(e) => onEditNameChange(e.target.value)}
                            fullWidth
                            autoFocus
                            helperText={t('spaces.name_placeholder')}
                        />
                        <TextField
                            size="small"
                            label={t('spaces.desc_label')}
                            value={editDescription}
                            onChange={(e) => onEditDescriptionChange(e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            helperText={t('spaces.desc_placeholder')}
                        />
                    </Stack>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
                        <IconButton size="small" onClick={onCancelEdit} color="default">
                            <Close fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={onSaveEdit} color="primary">
                            <Check fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            ) : (
                <>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {space.name}
                        </Typography>
                        {space.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                {space.description}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                        <IconButton size="small" onClick={() => onEdit(space)} sx={{ color: 'text.secondary' }}>
                            <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => onDelete(space)} sx={{ color: 'error.light' }}>
                            <Delete fontSize="small" />
                        </IconButton>
                    </Box>
                </>
            )}
        </Paper>
    );
};

interface SpacesManagementFormProps {
    open?: boolean;
    onClose?: () => void;
    userId: string;
    spaces: Space[];
    plants: Plant[];
    mode?: 'dialog' | 'inline';
}

const SpacesManagementForm: React.FC<SpacesManagementFormProps> = ({
    open = true,
    onClose,
    userId,
    spaces,
    plants,
    mode = 'dialog',
}) => {
    const { t } = useTranslation();
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [newName, setNewName] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [deleteConfirmSpace, setDeleteConfirmSpace] = useState<Space | null>(null);

    const sortedSpaces = [...spaces].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(TouchSensor, {
            activationConstraint: { delay: 250, tolerance: 5 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = sortedSpaces.findIndex(s => s.id === active.id);
        const newIndex = sortedSpaces.findIndex(s => s.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = [...sortedSpaces];
        const [removed] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, removed);

        try {
            await Promise.all(
                reordered.map((sp, index) =>
                    updateDoc(doc(db, 'spaces', sp.id), { order: index })
                )
            );
        } catch (error) {
            console.error('Error reordering spaces:', error);
        }
    };

    const handleStartEdit = (space: Space) => {
        setEditingId(space.id);
        setEditName(space.name);
        setEditDescription(space.description || '');
    };

    const handleSaveEdit = async () => {
        if (!editingId || !editName.trim()) return;
        try {
            await updateDoc(doc(db, 'spaces', editingId), {
                name: editName.trim(),
                description: editDescription.trim(),
            });
        } catch (error) {
            console.error('Error updating space:', error);
        }
        setEditingId(null);
        setEditName('');
        setEditDescription('');
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditName('');
        setEditDescription('');
    };

    const handleAddSpace = async () => {
        if (!newName.trim()) return;
        try {
            await addDoc(collection(db, 'spaces'), {
                userId,
                name: newName.trim(),
                description: newDescription.trim(),
                order: spaces.length,
                createdAt: new Date(),
            });
        } catch (error) {
            console.error('Error adding space:', error);
        }
        setNewName('');
        setNewDescription('');
        setShowAddForm(false);
    };

    const handleDeleteSpace = (space: Space) => {
        setDeleteConfirmSpace(space);
    };

    const confirmDelete = async () => {
        if (!deleteConfirmSpace) return;
        try {
            // Unassign plants in this space
            const plantsInSpace = plants.filter(p => p.spaceId === deleteConfirmSpace.id);
            await Promise.all(
                plantsInSpace.map(p =>
                    updateDoc(doc(db, 'plants', p.id), { spaceId: '' })
                )
            );
            await deleteDoc(doc(db, 'spaces', deleteConfirmSpace.id));
        } catch (error) {
            console.error('Error deleting space:', error);
        }
        setDeleteConfirmSpace(null);
    };

    const formContent = (
        <Box>
            {sortedSpaces.length === 0 && !showAddForm ? (
                <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {t('spaces.empty_state')}
                </Typography>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={sortedSpaces.map(s => s.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <Stack spacing={1}>
                            {sortedSpaces.map(space => (
                                <SortableSpaceItem
                                    key={space.id}
                                    space={space}
                                    onEdit={handleStartEdit}
                                    onDelete={handleDeleteSpace}
                                    editingId={editingId}
                                    editName={editName}
                                    editDescription={editDescription}
                                    onEditNameChange={setEditName}
                                    onEditDescriptionChange={setEditDescription}
                                    onSaveEdit={handleSaveEdit}
                                    onCancelEdit={handleCancelEdit}
                                />
                            ))}
                        </Stack>
                    </SortableContext>
                </DndContext>
            )}

            {showAddForm ? (
                <Paper elevation={1} sx={{ p: 1.5, mt: 1.5, borderRadius: 2, border: '1px dashed', borderColor: 'primary.light' }}>
                    <Stack spacing={1.5}>
                        <TextField
                            size="small"
                            label={t('spaces.name_label')}
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            fullWidth
                            autoFocus
                            placeholder={t('spaces.name_placeholder')}
                        />
                        <TextField
                            size="small"
                            label={t('spaces.desc_label')}
                            value={newDescription}
                            onChange={(e) => setNewDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            placeholder={t('spaces.desc_placeholder')}
                        />
                    </Stack>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                        <Button
                            size="small"
                            onClick={() => {
                                setShowAddForm(false);
                                setNewName('');
                                setNewDescription('');
                            }}
                        >
                            {t('spaces.cancel_btn')}
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleAddSpace}
                            disabled={!newName.trim()}
                        >
                            {t('spaces.add_btn')}
                        </Button>
                    </Box>
                </Paper>
            ) : (
                <Button
                    startIcon={<Add />}
                    onClick={() => setShowAddForm(true)}
                    sx={{ mt: 1.5 }}
                    fullWidth
                    variant="outlined"
                >
                    {t('spaces.add_space_btn')}
                </Button>
            )}

            {/* Delete confirmation dialog */}
            <Dialog open={!!deleteConfirmSpace} onClose={() => setDeleteConfirmSpace(null)}>
                <DialogTitle>{t('spaces.delete_title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('spaces.delete_confirm', { name: deleteConfirmSpace?.name })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmSpace(null)}>{t('spaces.cancel_btn')}</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">{t('spaces.delete_btn')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );

    if (mode === 'inline') {
        return formContent;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('spaces.title')}</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                {formContent}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} variant="contained" disabled={editingId !== null || showAddForm}>{t('spaces.done_btn')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default SpacesManagementForm;
